import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { AGE_OPTIONS } from '@/pages/Onboarding/options/Age/AGE_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { ETHNICITY_OPTIONS } from '../../../OwnAIMan/screens/Ethnicity/ETHNICITY_OPTIONS';
import { LayoutDataType } from '../../types';

import { ScreenAgeStyles } from './Age.styles';

const preloadArray = AGE_OPTIONS.reduce<string[]>((acc, ageOption) => {
  const ethnicityImages = ETHNICITY_OPTIONS.map(
    (ethnicityOption) =>
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-man/ethnicity/sprite-${ageOption.id}-${ethnicityOption.id}.png`,
  );

  acc = [...acc, ...ethnicityImages];

  return acc;
}, []);

export const ScreenAge: React.FC = () => {
  const { user } = useAccount();

  React.useEffect(() => {
    if (user && canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Age',
      });
    }
    preloadArray.forEach((src) => {
      preloadImage(src);
    });
  }, [user]);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const age = form.watch('age');

  const handleSelect = (option: OnbOptionType<LayoutDataType['age']>) => {
    form.setValue('age', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        age: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  return (
    <ScreenAgeStyles>
      <OnboardingNavigation isRenderBackButton={false} />
      <OwnAiTitle>
        Choose your AI partner's <OwnAiTitleHighlight>age</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {AGE_OPTIONS.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="age"
              isSelected={age === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-man/age/sprite-${option.id}.png`}
              title={option.title}
              TEST_ID={`age-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>
    </ScreenAgeStyles>
  );
};
